import { CSSObject, styled, Theme } from '@mui/material/styles'
import MuiDrawer from '@mui/material/Drawer'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'

import withResponsiveVisibility from '../../../hoc/withResponsiveVisibility'
import { mobile } from '../../../utils/breakpoints'

const openedMixin = (theme: Theme, drawerWidth: number): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('mobile')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
})

const StaffHeaderLogoBase = styled('img')`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  height: 44px;
  width: auto;
  object-fit: contain;
  pointer-events: none;

  ${({ theme }) => mobile(theme, { height: '32px' })}
`

export const StaffHeaderLogo = withResponsiveVisibility(StaffHeaderLogoBase)

export const StaffHeaderActionsContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1.25),
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  maxWidth: 170,
  [theme.breakpoints.up('tablet')]: {
    maxWidth: 320,
  },
}))

export const StaffAccountButton = styled(Button)(({ theme }) => ({
  minWidth: 0,
  maxWidth: 86,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  textTransform: 'none',
  flexShrink: 1,
  justifyContent: 'flex-start',
  '& .MuiButton-startIcon': {
    marginRight: 0,
  },
  '& .MuiButton-endIcon': {
    marginLeft: 0,
  },
  [theme.breakpoints.up('tablet')]: {
    maxWidth: 180,
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.5),
    },
    '& .MuiButton-endIcon': {
      marginLeft: theme.spacing(0.5),
    },
  },
}))

export const StaffAccountButtonText = styled('span')(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'none',
  fontSize: 14,
  [theme.breakpoints.up('tablet')]: {
    display: 'block',
    fontSize: 16,
  },
}))

export const StaffLanguageButton = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  gap: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  fontSize: 14,
  fontWeight: 600,
}))

export const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}))

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
  drawerWidth?: number
}

export const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'drawerWidth',
})<AppBarProps>(({ theme, open, drawerWidth }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

interface DrawerProps {
  open?: boolean
  drawerWidth: number
}

export const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' && prop !== 'drawerWidth' })<DrawerProps>(
  ({ theme, open, drawerWidth }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      zIndex: theme.zIndex.drawer + 2,
      ...openedMixin(theme, drawerWidth),
      '& .MuiDrawer-paper': {
        zIndex: theme.zIndex.drawer + 2,
        ...openedMixin(theme, drawerWidth),
        display: 'flex',
        flexDirection: 'column',
      },
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  })
)

export const DrawerVersionFooter = styled('div')(({ theme }) => ({
  marginTop: 'auto',
  padding: theme.spacing(1.5, 2),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontSize: theme.typography.caption.fontSize,
  letterSpacing: '0.02em',
}))
